import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { paymentEndpoints } from '../../services/api/endpoints/payment.endpoint';
import { logger } from '../../utils/logger';
import type {
  Invoice,
  PaymentSettings,
  WalletBalance,
  WalletTransaction,
  Payout,
  Refund,
  ListInvoicesParams,
  ListTransactionsParams,
  ListPayoutsParams,
  ListRefundsParams,
} from '../../types/payment';

// ==================== STATE INTERFACE ====================

interface PaymentState {
  // Payment Settings
  settings: PaymentSettings | null;
  settingsLoading: boolean;
  settingsError: string | null;

  // Invoices
  invoices: Invoice[];
  selectedInvoice: Invoice | null;
  invoicesLoading: boolean;
  invoicesError: string | null;
  invoicesTotalCount: number;
  invoicesCurrentPage: number;

  // Wallet
  walletBalance: WalletBalance | null;
  walletBalanceLoading: boolean;
  walletBalanceError: string | null;

  // Transactions
  transactions: WalletTransaction[];
  transactionsLoading: boolean;
  transactionsError: string | null;
  transactionsTotalCount: number;

  // Payouts
  payouts: Payout[];
  payoutsLoading: boolean;
  payoutsError: string | null;
  payoutsTotalCount: number;

  // Refunds
  refunds: Refund[];
  refundsLoading: boolean;
  refundsError: string | null;
  refundsTotalCount: number;
}

const initialState: PaymentState = {
  settings: null,
  settingsLoading: false,
  settingsError: null,

  invoices: [],
  selectedInvoice: null,
  invoicesLoading: false,
  invoicesError: null,
  invoicesTotalCount: 0,
  invoicesCurrentPage: 1,

  walletBalance: null,
  walletBalanceLoading: false,
  walletBalanceError: null,

  transactions: [],
  transactionsLoading: false,
  transactionsError: null,
  transactionsTotalCount: 0,

  payouts: [],
  payoutsLoading: false,
  payoutsError: null,
  payoutsTotalCount: 0,

  refunds: [],
  refundsLoading: false,
  refundsError: null,
  refundsTotalCount: 0,
};

// ==================== ASYNC THUNKS ====================

export const fetchPaymentSettings = createAsyncThunk(
  'payment/fetchSettings',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await paymentEndpoints.getPaymentSettings(organizationId);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch payment settings:', error);
      return rejectWithValue(error.message || 'Failed to fetch payment settings');
    }
  }
);

export const syncAccountStatus = createAsyncThunk(
  'payment/syncAccountStatus',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await paymentEndpoints.syncAccountStatus(organizationId);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to sync account status:', error);
      return rejectWithValue(error.message || 'Failed to sync account status');
    }
  }
);

export const fetchInvoices = createAsyncThunk(
  'payment/fetchInvoices',
  async (
    { organizationId, params }: { organizationId: string; params?: ListInvoicesParams },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentEndpoints.listInvoices(organizationId, params);
      console.log('fetchInvoices thunk - raw response:', response);
      console.log('fetchInvoices thunk - returning:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch invoices:', error);
      console.error('fetchInvoices thunk error:', error);
      return rejectWithValue(error.message || 'Failed to fetch invoices');
    }
  }
);

export const fetchInvoiceById = createAsyncThunk(
  'payment/fetchInvoiceById',
  async (
    { organizationId, invoiceId }: { organizationId: string; invoiceId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentEndpoints.getInvoiceById(organizationId, invoiceId);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch invoice:', error);
      return rejectWithValue(error.message || 'Failed to fetch invoice');
    }
  }
);

export const fetchInvoiceByBooking = createAsyncThunk(
  'payment/fetchInvoiceByBooking',
  async (
    { organizationId, bookingId }: { organizationId: string; bookingId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentEndpoints.getInvoiceByBooking(organizationId, bookingId);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch booking invoice:', error);
      return rejectWithValue(error.message || 'Failed to fetch booking invoice');
    }
  }
);

export const fetchWalletBalance = createAsyncThunk(
  'payment/fetchWalletBalance',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await paymentEndpoints.getWalletBalance(organizationId);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch wallet balance:', error);
      return rejectWithValue(error.message || 'Failed to fetch wallet balance');
    }
  }
);

export const fetchWalletTransactions = createAsyncThunk(
  'payment/fetchWalletTransactions',
  async (
    { organizationId, params }: { organizationId: string; params?: ListTransactionsParams },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentEndpoints.getWalletTransactions(organizationId, params);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch wallet transactions:', error);
      return rejectWithValue(error.message || 'Failed to fetch wallet transactions');
    }
  }
);

export const fetchPayouts = createAsyncThunk(
  'payment/fetchPayouts',
  async (
    { organizationId, params }: { organizationId: string; params?: ListPayoutsParams },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentEndpoints.listPayouts(organizationId, params);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch payouts:', error);
      return rejectWithValue(error.message || 'Failed to fetch payouts');
    }
  }
);

export const fetchRefunds = createAsyncThunk(
  'payment/fetchRefunds',
  async (
    { organizationId, params }: { organizationId: string; params?: ListRefundsParams },
    { rejectWithValue }
  ) => {
    try {
      const response = await paymentEndpoints.listRefunds(organizationId, params);
      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch refunds:', error);
      return rejectWithValue(error.message || 'Failed to fetch refunds');
    }
  }
);

// ==================== SLICE ====================

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setSelectedInvoice: (state, action: PayloadAction<Invoice | null>) => {
      state.selectedInvoice = action.payload;
    },
    clearErrors: state => {
      state.settingsError = null;
      state.invoicesError = null;
      state.walletBalanceError = null;
      state.transactionsError = null;
      state.payoutsError = null;
      state.refundsError = null;
    },
    resetPaymentState: () => initialState,
    clearInvoices: state => {
      state.invoices = [];
      state.selectedInvoice = null;
      state.invoicesTotalCount = 0;
      state.invoicesCurrentPage = 1;
    },
  },
  extraReducers: builder => {
    // ==================== PAYMENT SETTINGS ====================
    builder
      .addCase(fetchPaymentSettings.pending, state => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(fetchPaymentSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchPaymentSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload as string;
      });

    builder
      .addCase(syncAccountStatus.pending, state => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(syncAccountStatus.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload;
      })
      .addCase(syncAccountStatus.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload as string;
      });

    // ==================== INVOICES ====================
    builder
      .addCase(fetchInvoices.pending, state => {
        state.invoicesLoading = true;
        state.invoicesError = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        console.log('fetchInvoices.fulfilled - action.payload:', action.payload);
        state.invoicesLoading = false;
        state.invoices = action.payload.invoices;
        state.invoicesTotalCount = action.payload.total;
        state.invoicesCurrentPage = action.payload.page;
        console.log('fetchInvoices.fulfilled - state.invoices:', state.invoices);
        console.log(
          'fetchInvoices.fulfilled - state.invoicesTotalCount:',
          state.invoicesTotalCount
        );
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesError = action.payload as string;
      });

    builder
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.selectedInvoice = action.payload;
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.invoicesError = action.payload as string;
      });

    builder
      .addCase(fetchInvoiceByBooking.fulfilled, (state, action) => {
        state.selectedInvoice = action.payload;
      })
      .addCase(fetchInvoiceByBooking.rejected, (state, action) => {
        state.invoicesError = action.payload as string;
      });

    // ==================== WALLET ====================
    builder
      .addCase(fetchWalletBalance.pending, state => {
        state.walletBalanceLoading = true;
        state.walletBalanceError = null;
      })
      .addCase(fetchWalletBalance.fulfilled, (state, action) => {
        state.walletBalanceLoading = false;
        state.walletBalance = action.payload;
      })
      .addCase(fetchWalletBalance.rejected, (state, action) => {
        state.walletBalanceLoading = false;
        state.walletBalanceError = action.payload as string;
      });

    builder
      .addCase(fetchWalletTransactions.pending, state => {
        state.transactionsLoading = true;
        state.transactionsError = null;
      })
      .addCase(fetchWalletTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        state.transactions = action.payload.transactions;
        state.transactionsTotalCount = action.payload.total;
      })
      .addCase(fetchWalletTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.transactionsError = action.payload as string;
      });

    builder
      .addCase(fetchPayouts.pending, state => {
        state.payoutsLoading = true;
        state.payoutsError = null;
      })
      .addCase(fetchPayouts.fulfilled, (state, action) => {
        state.payoutsLoading = false;
        state.payouts = action.payload.payouts;
        state.payoutsTotalCount = action.payload.total;
      })
      .addCase(fetchPayouts.rejected, (state, action) => {
        state.payoutsLoading = false;
        state.payoutsError = action.payload as string;
      });

    // ==================== REFUNDS ====================
    builder
      .addCase(fetchRefunds.pending, state => {
        state.refundsLoading = true;
        state.refundsError = null;
      })
      .addCase(fetchRefunds.fulfilled, (state, action) => {
        state.refundsLoading = false;
        state.refunds = action.payload.refunds;
        state.refundsTotalCount = action.payload.total;
      })
      .addCase(fetchRefunds.rejected, (state, action) => {
        state.refundsLoading = false;
        state.refundsError = action.payload as string;
      });
  },
});

// ==================== ACTIONS ====================

export const { setSelectedInvoice, clearErrors, resetPaymentState, clearInvoices } =
  paymentSlice.actions;

// ==================== SELECTORS ====================

export const selectPayment = (state: { payment: PaymentState }) => state.payment;
export const selectPaymentSettings = (state: { payment: PaymentState }) => state.payment.settings;
export const selectInvoices = (state: { payment: PaymentState }) => state.payment.invoices;
export const selectSelectedInvoice = (state: { payment: PaymentState }) =>
  state.payment.selectedInvoice;
export const selectWalletBalance = (state: { payment: PaymentState }) =>
  state.payment.walletBalance;
export const selectTransactions = (state: { payment: PaymentState }) => state.payment.transactions;
export const selectPayouts = (state: { payment: PaymentState }) => state.payment.payouts;
export const selectRefunds = (state: { payment: PaymentState }) => state.payment.refunds;

export default paymentSlice.reducer;
